import {
  get, isString, includes, first,
} from 'lodash-es';
import { VUE_APP_THUMBNAILS_CLOUDFRONT_DISTRIBUTION_URL } from '@/lib/constants/app';
import { moduleTypeLabels, APP_COPY_DATA_TYPE, USER_UPLOADABLE_ASSET_TYPES } from '@/lib/constants/index';
import i18next from '@/lib/i18next';
import defaultThumbnailUrl from '@/assets/blank_board.png';

/**
 * Decode HTML entities. Since <textarea> cannot contain html, there is no risk of XSS.
 * https://stackoverflow.com/a/1395954/8373513
 * @param {string} html - The html to decode
 */
export function decodeHTML(html) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html || '';
  return textarea.value;
}

/**
 * clean up encoding for an orion text module
 */
export function cleanOrionTextModule(mod) {
  const updatedModule = mod;
  if (updatedModule.type === 'text' && updatedModule.options.data_type === 'orion') {
    updatedModule.options.content = decodeHTML(updatedModule.options.content);
  }

  return updatedModule;
}

/** Only encodes <, >, & */
export function encodeHTML(html) {
  if (!html || typeof html !== 'string') return '';

  return html.replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Get duration string 00:00:00 for seconds up to 99 hours
 * @param s - seconds
 */
export function secondsToDuration(s) {
  if (!s) return '00:00:00';

  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s - (hours * 3600)) / 60);
  let seconds = s - (hours * 3600) - (minutes * 60);

  seconds = Math.round(Math.round(seconds * 100) / 100);

  let result = '';

  if (hours > 1) {
    result += `${hours < 10 ? '0' : ''}${hours}:`;
  }

  result += `${minutes < 10 ? '0' : ''}${minutes}:`;
  result += `${seconds < 10 ? '0' : ''}${seconds}`;
  return result;
}

export function snakeToPascal(str) {
  if (!str) return '';

  const words = str.split('_');

  return words
    .map((w) => `${w[0].toUpperCase()}${w.substr(1)}`)
    .join('');
}

/**
 * Strip tags from an html input. This is adapted from the nunjucks "striptags" filter:
 * https://github.com/mozilla/nunjucks/blob/master/nunjucks/src/filters.js
 * @param {string} html - The html to strip
 */
export function stripTags(html) {
  let str = html;
  if (html === null || html === undefined || html === false) {
    str = '';
  }

  const tags = /<\/?([a-z][a-z0-9]*)\b[^>]*>|<!--[\s\S]*?-->/gi;

  return str.replace(tags, '') // strip tags
    .replace(/^\s*|\s*$/g, '') // strip leading/trailing space
    .replace(/\s+/gi, ' '); // squash adjacent spaces
}

export function moduleTypeToComponent(type) {
  return `${snakeToPascal(type)}Module`;
}

export function moduleTypeToOptions(type) {
  return `${snakeToPascal(type)}ModuleOptions`;
}

export function moduleTypeToLabel(type) {
  if (!type) return '';

  if (moduleTypeLabels()[type]) return moduleTypeLabels()[type];

  const words = type.split('_');

  return words
    .map((w) => `${w[0].toUpperCase()}${w.substr(1)}`)
    .join(' ');
}

export function parseDateString(dateString) {
  if (!isString(dateString)) return null;

  // The backend sends timestamps in UTC but without the Z at the end.
  // If the timestamp already has Z or a timezone like +00:00, leave it;
  // otherwise add the Z.
  const timestampHasTimezone = dateString.includes('Z') || /[+-]\d{2}:\d{2}$/.test(dateString);
  return new Date(timestampHasTimezone ? dateString : `${dateString}Z`);
}

/**
 * Format a date into a human-readable format
 * @param {string|date} date - The date as an ISO string or instance of Date
 * @param {boolean} includeTime - Whether to include formatted time in the output
 */
export function formatDate(date, includeTime, includeDate = true) {
  if (!date) return '';
  let parsedDate = date;
  const formattedDate = [];

  try {
    if (typeof date === 'string') {
      parsedDate = parseDateString(date);
    }
    const locale = i18next.language || 'en';

    if (includeDate) {
      formattedDate.push(parsedDate.toLocaleDateString(locale));
    }

    if (includeTime) {
      formattedDate.push(`${parsedDate.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
      })}`);
    }
    return formattedDate.join(' ');
  } catch (e) {
    throw new Error(e);
  }
}

export function formatPageTitle(page, idx, draftType) {
  // If we've got a title already, that's great; return it with decoded HTML entities
  if (page && page.title) return decodeHTML(page.title);
  // Otherwise make a default page title like "Slide 4"
  return draftType === 'board'
    ? i18next.t('Page %(idx)s', { idx: idx + 1 })
    : i18next.t('Slide %(idx)s', { idx: idx + 1 });
}

export function toInitial(str) {
  if (!str) return '';

  return str.charAt(0);
}

/**
 * Check a string to see if it is a valid guid.
 * @param {string} str - The string to test
 * @returns {boolean} Whether the stripped string is a guid
 */
export function isGuid(str) {
  const guidRegex = /^[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12}$/i;
  return guidRegex.test(str.trim());
}

const inputTypes = ['text', 'password', 'number', 'email', 'tel', 'url', 'search',
  'date', 'datetime', 'datetime-local', 'time', 'month', 'week'];

export function isTextInput(element) {
  const tagName = element.tagName.toLowerCase();
  if (tagName === 'textarea') return true;
  if (tagName === 'input') {
    const type = get(element, 'type', '').toLowerCase();
    if (inputTypes.includes(type)) return true;
  }
  if (element.className.includes('ProseMirror')) return true;
  return false;
}

export function isMac() {
  return /Mac/i.test(navigator.userAgent);
}

export function isIOS() {
  const isIOSDevice = (/iPhone|iPad|iPod/i.test(navigator.userAgent));
  const isIpadPro = navigator.maxTouchPoints && navigator.maxTouchPoints > 1 && isMac();
  return isIOSDevice || isIpadPro;
}

export function isMobileOrTablet() {
  const mobileOrTablet = (/Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  return mobileOrTablet || isIOS();
}

export async function getPageThumbnail(page, defaultThumbnail) {
  const thumbnailCloudfrontUrl = VUE_APP_THUMBNAILS_CLOUDFRONT_DISTRIBUTION_URL;
  let thumbnail = {};
  return fetch(`${thumbnailCloudfrontUrl + page?.id}-small`, { method: 'HEAD' })
    .then((response) => {
      if (!response.ok) {
        throw response;
      }
      thumbnail = { url: response.url, isStudioThumbnail: false };
      return thumbnail;
    })
    .catch(() => {
      thumbnail = { url: get(page, 'thumbnail.url', defaultThumbnail), isStudioThumbnail: true };
      return thumbnail;
    });
}

export async function getRefThumbnail(draft, page) {
  const revisionId = draft.references.find(
    (ref) => ref.reference_type === 'asset' && ref.reference_subtype === 'revision',
  ).reference_id;

  const headId = draft.references.find(
    (ref) => ref.reference_type === 'asset' && ref.reference_subtype === 'head',
  ).reference_id;

  const thumbnailCloudfrontUrl = VUE_APP_THUMBNAILS_CLOUDFRONT_DISTRIBUTION_URL;
  const revisionThumbUrl = `${thumbnailCloudfrontUrl}${revisionId}-large`;
  const headThumbUrl = `${thumbnailCloudfrontUrl}${headId}-large`;
  const pageThumbUrl = `${thumbnailCloudfrontUrl}${page?.id}-large`;

  return new Promise((resolve, reject) => {
    // if the revision thumb exists, use that
    fetch(revisionThumbUrl, { method: 'HEAD' })
      .then((response) => {
        if (!response.ok) {
          throw response;
        }
        const thumbnail = { url: response.url };
        resolve(thumbnail);
      })
      .catch(() => { // eslint-disable-line
        // otherwise use the head thumb
        fetch(headThumbUrl, { method: 'HEAD' })
          .then((response) => {
            if (!response.ok) {
              throw response;
            }
            const thumbnail = { url: response.url };
            resolve(thumbnail);
          })
          .catch(() => { // eslint-disable-line
            // no head thumb? use the page thumb
            const fallbackThumb = { url: get(page, 'thumbnail.url', defaultThumbnailUrl) };
            if (!page?.id && fallbackThumb) {
              resolve(fallbackThumb);
            } else {
              fetch(pageThumbUrl, { method: 'HEAD' })
                .then((response) => {
                  if (!response.ok) {
                    throw response;
                  }
                  const thumbnail = { url: response.url };
                  resolve(thumbnail);
                })
                .catch((e) => {
                  reject(e);
                });
            }
          });
      });
  });
}

export function canUseWebAnimationsApi() {
  return !!document.createElement('div').animate;
}

export function getSegmentObjFromExperimentData(experimentObj) {
  // Remove nested object
  // Move experiment_id and variations into in the same obj for segment to use the data
  const segmentFormat = {
    experiment_id: experimentObj?.code,
    variation_id: experimentObj.variation?.variation_id,
    variation_code: experimentObj.variation?.variation_code,
    // Change value to a decimal percent for segment
    variation_percentage: (experimentObj.variation?.variation_percentage) / 100,
    variation_is_control: experimentObj.variation?.variation_is_control,
  };

  return segmentFormat;
}

export function durationToSeconds(video) {
  if (includes(get(this, video), ':')) {
    return get(this, video).split(':')
      .reduce((acc, time) => (60 * acc) + +time);
  }

  if (get(this, video)) {
    return Number(get(this, video));
  }
  return null;
}

/**
 * Finds the last descendant node that has content in document order.
 * @param {Node} sourceNode Node to search.
 */
export function lastNodeWithContent(sourceNode) {
  let last;
  const tagName = (node) => node.tagName?.toLowerCase();
  /*
    Elements considered to have content regardless of what's inside.
  */
  const elements = new Set(['table']);
  const findLastTextNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent.trim().length > 0) {
        last = node;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (elements.has(tagName(node))) {
        last = node;
      } else if (node.hasChildNodes()) {
        node.childNodes.forEach((child) => findLastTextNode(child));
      }
    }
  };
  findLastTextNode(sourceNode);
  return last;
}

/**
 * Removes trailing descendant nodes that do not have content.
 * @param {Node} source Node to trim child nodes from.
 */
export function trimHtml(source) {
  const target = document.createElement('div');
  const lastNode = lastNodeWithContent(source);
  const trim = (sourceNode, targetNode) => {
    if (sourceNode.hasChildNodes()) {
      for (let index = 0; index < sourceNode.childNodes.length; index += 1) {
        const sourceChild = sourceNode.childNodes[index];
        if (sourceChild.nodeType === Node.TEXT_NODE) {
          targetNode.appendChild(document.createTextNode(sourceChild.textContent));
        } else if (sourceChild.nodeType === Node.ELEMENT_NODE) {
          const targetChild = document.createElement(sourceChild.tagName);
          targetChild.className = sourceChild.className;
          targetNode.appendChild(targetChild);
          if (!trim(sourceChild, targetChild)) {
            return false;
          }
        }
        /*
          If we've found the last node with content then exit. All nodes after
          this node will not be included.
        */
        if (sourceChild === lastNode) {
          return false;
        }
      }
    }
    return true;
  };
  trim(source, target);
  return target.innerHTML;
}

/**
 * Extract supported elements from html content. Preserve document structure
 * while exlcuding elements that are not supported and removing node attributes.
 * @param {String} html Html string to extract from.
 * @param {Set} elements Element names that should be extracted.
 * @param {Boolean} trim If true, will remove trailing descendant nodes without content.
 * @param {Set} allowedClasses HTML class names that should be extracted
 */
export function extractHtml(html, elements = new Set(['br', 'div', 'span']), trim = true, allowedClasses = null) {
  const source = document.createElement('div');
  const target = document.createElement('div');
  source.innerHTML = html || '';
  const tagName = (node) => node.tagName?.toLowerCase();
  /*
    Clones the content from the sourceNode to the targetNode.
    Removes all node attributes and unsupported elements.
  */
  const extract = (sourceNode, targetNode) => {
    const sourceTag = tagName(source);
    if (sourceNode.hasChildNodes() && elements.has(sourceTag)) {
      sourceNode.childNodes.forEach((sourceChild) => {
        if (sourceChild.nodeType === Node.TEXT_NODE) {
          const targetChild = document.createTextNode(sourceChild.textContent);
          targetNode.appendChild(targetChild);
        } else if (sourceChild.nodeType === Node.ELEMENT_NODE) {
          const childTag = tagName(sourceChild);
          if (elements.has(childTag)) {
            const targetChild = document.createElement(childTag);
            extract(sourceChild, targetChild);

            // Extract any allowed classes
            if (allowedClasses) {
              sourceChild.classList.forEach((cls) => {
                if (allowedClasses.has(cls)) {
                  targetChild.classList.add(cls);
                }
              });
            }

            targetNode.appendChild(targetChild);
          }
        }
      });
    }
  };
  extract(source, target);

  if (trim) {
    return trimHtml(target);
  }

  return target.innerHTML;
}

/**
 * Extract text content from html content.
 */
export function extractTextFromHtml(html) {
  const source = document.createElement('div');
  source.innerHTML = html || '';
  return source.textContent;
}

/*
Check if studio is an iframe
*/
export function isIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

export function getBrowserZoomValue() {
  if (window) {
    return Math.round((window.outerWidth / window.innerWidth) * 100);
  }
  return false;
}

export function handleTextInputPaste(e) {
  try {
    // Parse the data as JSON
    const parsedData = JSON.parse(e.clipboardData.getData('text'));
    if (parsedData.type === APP_COPY_DATA_TYPE) {
      e.preventDefault();
    }
    return true;
  } catch (error) {
    // If parsing fails, it means the data is not JSON, so we can ignore
    return false;
  }
}

export function assetIsUpload(asset) {
  try {
    return asset && asset.is_user_created
    && (
      (get(asset, 'type.id') && USER_UPLOADABLE_ASSET_TYPES.includes(asset.type.id))
      || (asset.type_id && USER_UPLOADABLE_ASSET_TYPES.includes(asset.type_id))
    );
  } catch {
    return false;
  }
}

/**
 * Gets the client event point for desktop or mobile.
 * @param {Object} event Browser event object.
 */
export function getClientPoint(event) {
  const clientX = first(event.targetTouches)?.clientX || event.clientX;
  const clientY = first(event.targetTouches)?.clientY || event.clientY;
  return { clientX, clientY };
}

/**
 * Converts a hexadecimal color to RGBA format
 * @param {string} hex - Hex color string (e.g., '#RGB', '#RRGGBB', '#RRGGBBAA', 'RGB', 'RRGGBB', 'RRGGBBAA').
 *                       Can include or omit the '#' prefix. If 8-digit hex is provided, the alpha channel from the hex value will be used.
 * @param {number} [opacity=1] - Optional opacity/alpha value in the range of 0-1 (e.g., 0.5 for 50% opacity, 1 for fully opaque).
 *                               Ignored if an 8-digit hex value with alpha channel is provided.
 * @returns {string} - RGBA color string (e.g., 'rgba(255, 0, 0, 0.8)')
 */
export function formatHEXToRGBA(hex, opacity = 1) {
  // Remove '#' if present
  let cleanHex = hex.startsWith('#') ? hex.slice(1) : hex;

  // Handle 3-digit shorthand hex codes
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map(char => char + char).join('');
  }

  // Parse each color component
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  // Check if it's an 8-digit hex value (includes alpha channel)
  let alpha = opacity;
  if (cleanHex.length === 8) {
    // Extract alpha channel from last 2 characters and convert from hex (0-255) to decimal (0-1)
    const alphaHex = parseInt(cleanHex.substring(6, 8), 16);
    alpha = (alphaHex / 255).toFixed(2);
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function formatColorToRGBA(colorValue, opacityValue) {
  if (!colorValue) return '';
  let rgbaColor = '';
  if (colorValue.startsWith('#')) {
    // Hex color - use existing utility function
    rgbaColor = formatHEXToRGBA(colorValue, opacityValue);
  } else if (colorValue.startsWith('rgb')) {
    // RGB or RGBA color
    if (colorValue.startsWith('rgba')) {
      // Replace existing alpha value (last number before closing paren)
      rgbaColor = colorValue.replace(/,\s*[\d.]+\)$/, `, ${opacityValue})`);
    } else {
      // Convert rgb to rgba by adding alpha channel
      rgbaColor = colorValue.replace('rgb(', 'rgba(').replace(')', `, ${opacityValue})`);
    }
  } else {
    // Unknown format or null, use as-is
    rgbaColor = colorValue;
  }

  return rgbaColor;
}

export function getFilenameExtension(filename, { withDot = false }) {
  if (!filename) return null;
  const ext = filename.split('.').pop().toLowerCase();
  return withDot ? `.${ext}` : ext;
}

/**
 * Converts any color format (hex, rgb, rgba) to full 6-digit or 8-digit hexadecimal format
 * @param {string} color - The color to convert (e.g., '#fff', '#ffffff', '#ffffffff', 'rgb(255,255,255)', 'rgba(255,255,255,0.5)')
 * @param {number} [alpha] - Optional alpha/opacity value to append as hex alpha channel. Must be in the range of 0-1 (e.g., 0.5 for 50% opacity, 1 for fully opaque)
 * @returns {string} - Full 6-digit or 8-digit hex color (e.g., '#FFFFFF' or '#FFFFFFFF')
 */
export function convertColorToHex(color, alpha) {
  // Already an 8-digit hex (includes alpha)
  if (/^#[0-9A-F]{8}$/i.test(color)) {
    return color.toUpperCase();
  }

  // Already a 6-digit hex
  if (/^#[0-9A-F]{6}$/i.test(color)) {
    const hexColor = color.toUpperCase();
    // If alpha is provided, append it as hex
    if (alpha !== undefined && alpha !== null) {
      const alphaHex = Math.round(alpha * 255).toString(16).padStart(2, '0').toUpperCase();
      return `${hexColor}${alphaHex}`;
    }
    return hexColor;
  }

  // 3-digit hex - expand it
  if (/^#[0-9A-F]{3}$/i.test(color)) {
    const r = color[1];
    const g = color[2];
    const b = color[3];
    const hexColor = `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
    // If alpha is provided, append it as hex
    if (alpha !== undefined && alpha !== null) {
      const alphaHex = Math.round(alpha * 255).toString(16).padStart(2, '0').toUpperCase();
      return `${hexColor}${alphaHex}`;
    }
    return hexColor;
  }

  // rgb/rgba format
  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1], 10).toString(16).padStart(2, '0');
    const g = parseInt(rgbaMatch[2], 10).toString(16).padStart(2, '0');
    const b = parseInt(rgbaMatch[3], 10).toString(16).padStart(2, '0');

    // Check if alpha is provided in the color string (rgba) or as parameter
    const colorAlpha = rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : null;
    const finalAlpha = alpha !== undefined && alpha !== null ? alpha : colorAlpha;

    if (finalAlpha !== null && finalAlpha !== undefined) {
      const alphaHex = Math.round(finalAlpha * 255).toString(16).padStart(2, '0');
      return `#${r}${g}${b}${alphaHex}`.toUpperCase();
    }

    return `#${r}${g}${b}`.toUpperCase();
  }

  // Fallback: return as-is
  return color;
}

/**
 * Formats input to be a valid hexadecimal color format
 * - Auto-adds '#' as the first character if missing
 * - Removes any invalid characters (only allows 0-9, A-F, a-f)
 * - Limits to 7 characters total (# + 6 hex digits)
 * @param {string} value - The input value to format
 * @returns {string} - Formatted hex color string (e.g., '#FFFFFF')
 */
export function formatHexColorInput(value) {
  if (!value) {
    return '';
  }

  // Remove all characters that aren't # or valid hex characters
  let cleaned = value.replace(/[^#0-9A-Fa-f]/g, '');

  // Remove any # that isn't at the start
  const parts = cleaned.split('#');
  if (parts.length > 1) {
    // Keep only the first # and concatenate the rest
    cleaned = `#${parts.filter((p) => p).join('')}`;
  }

  // Ensure # is at the beginning
  if (cleaned && !cleaned.startsWith('#')) {
    cleaned = `#${cleaned}`;
  }

  // Limit to 7 characters (# + 6 hex digits)
  if (cleaned.length > 7) {
    cleaned = cleaned.substring(0, 7);
  }

  return cleaned.toUpperCase();
}
